import { useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import clsx from 'clsx'
import { FormProvider, Resolver, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import * as yup from 'yup'

import locationApi from '@/api/admin/locationApi'
import uploadApi from '@/api/admin/upload'
import areaApi from '@/api/config/areaApi'
import controllerDataConfigApi from '@/api/config/controllerDataConfig'
import emitterDataConfigApi from '@/api/config/emitterDataConfig'
import pipeDataConfigApi from '@/api/config/pipeDataConfigApi'
import plantDataConfigApi from '@/api/config/plantDataConfigApi'
import soilDataConfigApi from '@/api/config/soilDataConfigApi'
import stationDataConfigApi from '@/api/config/stationDataConfig'
import ButtonFieldError from '@/components/form-control/ButtonFieldError'
import ErrorMessage from '@/components/form-control/ErrorMessage'
import InputLabelField from '@/components/form-control/InputLabelField'
import Select from '@/components/form-control/Select'
import TimeFieldControl from '@/components/form-control/TimeFieldControl'
import {
  ChangePositionData,
  StationData,
} from '@/components/google-map/MapPositionStation'
import { CloseIcon, LocalPinIcon, UploadImage } from '@/components/icons'
import { STATION_GROUP_ID } from '@/constant/station'
import { useSearchSiteId } from '@/features/admin/hooks/siteInterface'
import { useAuthContext } from '@/features/auth/context'
import { useConfigUI } from '@/features/config/context'
import {
  useAddStation,
  useStationByid,
  useUpdateStation,
} from '@/features/config/hooks/station'
import useStationDecoderSync from '@/features/config/hooks/station/useStationDecoderSync'
import { NotifyService } from '@/helpers'
import { useTranslation } from '@/hooks/useTranslation'
import { useUnits } from '@/hooks/useUnits'
import { Emitter, EtZone, Pipe, Plant } from '@/types'
import { AreaParentType, SubAreaParentType } from '@/types/areaZone'
import { Controller } from '@/types/controller'
import { Soil } from '@/types/soil'
import { decoderLinkFields } from '@/utils/decoderLink'
import { fetchStationDecoderPurposeIds } from '@/utils/stationDecoderPurposes'
import { formatPosition } from '@/ultils'

import PinStationOnMap from './PinStationOnMap'
import { StationDeletionAction } from './StationDeletionAction'
import {
  buildStationUpdatePayload,
  mapStationDetailToFormValues,
  STATION_TYPE_OPTIONS,
} from './stationEditMapping'
import classes from './styles.module.scss'

interface StationsViewProps {
  id?: string
}

const isIrrigationType = (val: any) => {
  const v = typeof val === 'object' ? val?.value : val
  return v === 2
}

const createSchema = (t: (key: string) => string) =>
  yup.object({
    Type: yup
      .mixed()
      .required(t('formsValidationStationTypeRequired')),

    priority: yup.number().when('Type', {
      is: isIrrigationType,
      then: (s) =>
        s
          .typeError(t('formsValidationPriorityNumber'))
          .required(t('formsValidationPriorityRequired')),
      otherwise: (s) => s.notRequired().nullable(),
    }),

    controller: yup.mixed().required(t('formsValidationDecoderRequired')),

    areaParent: yup.mixed().required(t('formsValidationAreaRequired')),

    subAreaParent: yup
      .mixed()
      .test(
        'isRequired',
        t('formsValidationSubAreaRequired'),
        function (value) {
          if (!value || typeof value !== 'object') return false
          const { id, name } = value as any
          return Boolean(id && id !== 0) || Boolean(name?.trim())
        },
      ),

    pipe: yup.mixed().when('Type', {
      is: isIrrigationType,
      then: (s) => s.required(t('formsValidationPipeRequired')),
      otherwise: (s) => s.notRequired(),
    }),

    plant: yup.mixed().when('Type', {
      is: isIrrigationType,
      then: (s) => s.required(t('formsValidationPlantRequired')),
      otherwise: (s) => s.notRequired(),
    }),

    soil: yup.mixed().when('Type', {
      is: isIrrigationType,
      then: (s) => s.required(t('formsValidationSoilRequired')),
      otherwise: (s) => s.notRequired(),
    }),

    emitter: yup.mixed().when('Type', {
      is: isIrrigationType,
      then: (s) => s.required(t('formsValidationEmitterRequired')),
      otherwise: (s) => s.notRequired(),
    }),

    preciptation: yup
      .number()
      .transform((value, orig) => (String(orig).trim() === '' ? undefined : value))
      .when('Type', {
        is: isIrrigationType,
        then: (s) =>
          s.typeError(t('formsValidationPreciptationNumber')).min(0),
        otherwise: (s) => s.notRequired(),
      }),

    expected: yup
      .number()
      .transform((value, orig) => (String(orig).trim() === '' ? undefined : value))
      .when('Type', {
        is: isIrrigationType,
        then: (s) =>
          s.typeError(t('formsValidationExpectedFlowNumber')).min(0),
        otherwise: (s) => s.notRequired(),
      }),
    defaultRunTime: yup
      .string()
      .test(
        'max-time',
        t('formsValidationDefaultRuntimeMaxTime'),
        function (value) {
          if (!value) return true
          const timeParts = value.split(':').map(Number)
          if (timeParts.length !== 3) return false
          const [hours, minutes, seconds] = timeParts
          return hours < 17 || (hours === 17 && minutes <= 59 && seconds <= 59)
        },
      ),
  })

interface IFormControls {
  id: number
  priority: number
  /** The station name. `description` is what the server and the grid call it. */
  description: string
  image: string
  pictureFileName: string
  grsCoordinatedLatitude?: number | null
  grsCoordinatedLongitude?: number | null
  floorId?: number
  controller: Controller
  plant: Plant
  soil: Soil
  pipe: Pipe
  emitter: Emitter
  etZone: EtZone
  areaParent: AreaParentType
  subAreaParent: SubAreaParentType
  water: string
  surface: string
  preciptation: any
  plant_water: string
  expected: any
  landscape: number
  runtime: number
  siteId?: number | null
  siteName?: string | null
  locationId?: number | null
  locationName?: string | null
  defaultRunTime: string
  subAreaId?: number | null
  subArea: {
    id: number
    interfaceId: number
    name: string
    parentArea: {
      id: number
      interfaceId: number
      name: string
      parenArea: null
    }
  }
  Type: OptionType | null
}

const defaultValues: Partial<IFormControls> = {
  id: undefined,
  priority: undefined,
  description: '',
  image: '',
  pictureFileName: '',
  grsCoordinatedLatitude: undefined,
  grsCoordinatedLongitude: undefined,
  floorId: undefined,
  controller: undefined,
  plant: undefined,
  soil: undefined,
  pipe: undefined,
  emitter: undefined,
  etZone: undefined,
  areaParent: undefined,
  subAreaParent: undefined,
  water: '',
  surface: '',
  preciptation: '',
  plant_water: '',
  expected: '',
  landscape: undefined,
  runtime: undefined,
  siteId: undefined,
  siteName: '',
  locationId: undefined,
  locationName: '',
  defaultRunTime: '',
  subAreaId: undefined,
  // The option itself, not its bare value: the select matches on the object,
  // so a bare 2 left the field showing its placeholder while the save quietly
  // wrote Irrigation. Declared inline — STATION_TYPE_OPTIONS comes later.
  Type: { label: 'Irrigation', value: 2 },
} as any

interface LOCATION_VIEW {
  id: number
  interfaceId: number
  name: string
  numOfFloorOverlays: number
  numOfFloors: number
  siteId: number
}

interface StationSchema {
  priority: number
  controller: any
  pipe: any
  plant: any
  emitter: any
  areaParent: any
  subAreaParent?: any
  soil: any
  preciptation: number
  expected: number
  defaultRunTime: string
}

type OptionType = {
  label: string
  value: number
}

const StationsView = ({ id }: StationsViewProps) => {
  const [openPinStationOnMap, setOpenPinStationOnMap] = useState(false)
  const [pinStationOnMapData, setPinStationOnMapData] = useState<StationData>()
  const [image, setImage] = useState('')
  const [isLoadingIMG, setIsLoadingIMG] = useState(false)
  const { closeSidebar } = useConfigUI()
  const { interfaceId } = useAuthContext()
  const { mutateAsync: addStation, isLoading: loadingAddStation } =
    useAddStation()
  const {
    convertLabelUnit,
    convertFromMetricRawV2,
    convertToMetricRawV2,
  } = useUnits()
  const { t } = useTranslation()
  const { mutateAsync: editStation, isLoading: loadingEditStation } =
    useUpdateStation()
  const { saveWithDecoder, syncAfterCreate, isSyncingDecoder } =
    useStationDecoderSync()
  const { data: editItem, isLoading: isLoadingStation } = useStationByid(
    interfaceId,
    id,
  )
  const [loadOptionInitKey, setLoadOptionInitKey] = useState('')

  const isLoading = loadingAddStation || loadingEditStation || isSyncingDecoder

  const form = useForm<IFormControls>({
    mode: 'onTouched',
    defaultValues: defaultValues as unknown as IFormControls,
    resolver: yupResolver(createSchema(t)) as unknown as Resolver<
      IFormControls,
      any
    >,
    shouldUnregister: false,
  })
  const {
    formState: { errors },
  } = form

  const { reset, watch, getValues, setValue } = form

  const rawType = watch('Type')

  const type =
    rawType && typeof rawType === 'object'
      ? (rawType as OptionType)?.value
      : rawType

  useEffect(() => {
    if (id || type !== 1) return

    setValue('preciptation', '')
    setValue('expected', '')
    setValue('priority', 0)
    setValue('defaultRunTime', '')
    setValue('water', '')
  }, [id, setValue, type])

  const handleImgUpload = async (event: any) => {
    const file = event.target.files[0]
    const filename = file.name
    if (!file) {
      toast.error(t('stationsFileUploadInvalid'))
      return
    }
    setIsLoadingIMG(true)
    const data = new FormData()
    data.append('File', file)
    const dataRes = await uploadApi
      .uploadToS3(data)
      .then((r) => r.data)
      .catch(() => {
        toast.error(t('stationsUploadFailed'))
        return null
      })
    setIsLoadingIMG(false)
    if (dataRes) {
      setImage(filename)
      form.setValue('image', dataRes.s3Url)
      form.setValue('pictureFileName', dataRes.fileName)
    }
  }

  const handleSave = async (data: IFormControls) => {
    const {
      surface = 0,
      water = 0,
      plant_water = 0,
      preciptation = 0,
      expected = 0,
    } = data

    const type =
      typeof data.Type === 'object' ? (data.Type as any)?.value : data.Type

    const isIrrigation = type === 2

    // SCRUM-1145 — the decoder follows the station's Type.
    const decoderFields = {
      stationType: Number(type) || 2,
      decoder: data.controller as any,
      description: data.description?.trim(),
    }

    if (!id) {
      const created = await addStation({
        interfaceId: Number(interfaceId),
        priority: isIrrigation ? data.priority || 0 : 0,
        description: data.description?.trim() || '',
        pictureUrl: data.image || '',
        pictureFileName: data.pictureFileName || '',
        grsCoordinatedLatitude: data.grsCoordinatedLatitude || null,
        grsCoordinatedLongitude: data.grsCoordinatedLongitude || null,
        floorId: data.floorId || null,
        surfaceAccumulation:
          convertToMetricRawV2(Number(surface), 'mm', 'in') || 0,
        waterRequired: convertToMetricRawV2(Number(water), 'mm', 'in') || 0,
        plantAvailWater:
          convertToMetricRawV2(Number(plant_water), 'mm', 'in') || 0,
        precipitationRate: isIrrigation
          ? convertToMetricRawV2(Number(preciptation), 'mm/h', 'in/h') || 0
          : 0,
        expectedFlow: isIrrigation
          ? convertToMetricRawV2(Number(expected), 'l/m', 'gpm') || 0
          : 0,
        landscapeFactor: data.landscape || 0,
        rtDayOverride: Number(data.runtime) || 0,
        ...decoderLinkFields(data.controller),
        // Null, not 0: a non-irrigation station has no plant or soil, and 0 is
        // a foreign key matching no row — the create was refused for it.
        plantId: Number(data.plant?.id) || null,
        ...(isIrrigation && { pipeId: data.pipe?.id || 0 }),
        soilId: Number(data.soil?.id) || null,
        emitterId: (data.emitter?.id as any) || null,
        etZoneId: Number(data.etZone?.id) || null,
        stationGroupId: STATION_GROUP_ID,
        defaultRunTime: data.defaultRunTime || '00:00:00',
        subAreaId: (data.subAreaParent?.id as any) || 0,
        twiId: data.controller?.twiId || 0,
        Type: type || 2,
      })
        .then(() => true)
        .catch((error) => {
          console.error('Station create failed:', error)
          return false
        })
      if (created) await syncAfterCreate(decoderFields)
    } else {
      if (!editItem) return

      await saveWithDecoder(decoderFields, () =>
        editStation(
          buildStationUpdatePayload({
            detail: editItem,
            formValues: data,
            interfaceId: Number(interfaceId),
            stationGroupIdFallback: STATION_GROUP_ID,
            convertFromMetric: convertFromMetricRawV2,
            convertToMetric: convertToMetricRawV2,
          }),
        ),
      )
    }
  }

  const onChangePosition = (data: ChangePositionData) => {
    setValue('floorId', data.floorId)
    setValue('siteId', data.siteId)
    setValue('siteName', data.siteName)
    setValue('locationId', data.locationId)
    setValue('locationName', data.locationName)
    setValue('grsCoordinatedLatitude', data.grsCoordinatedLatitude)
    setValue('grsCoordinatedLongitude', data.grsCoordinatedLongitude)
    // setValue('areaParent', {
    //   id: data.areaId,
    //   name: data.areaName || '',
    // })
    // setValue('subAreaParent', {
    //   id: data.subAreaId,
    //   name: data.subAreaName || '',
    // })
    onClosePinStation()
  }

  const onClosePinStation = () => {
    setOpenPinStationOnMap(false)
    setPinStationOnMapData(undefined)
  }

  const [etZone, emitter, controller] = watch([
    'etZone',
    'emitter',
    'controller',
  ])
  const [grsCoordinatedLatitude, grsCoordinatedLongitude] = watch([
    'grsCoordinatedLatitude',
    'grsCoordinatedLongitude',
  ])

  const { data: dataSite } = useSearchSiteId(interfaceId + '', { PageSize: 0 })
  const [dataLocation, setDataLocation] = useState<LOCATION_VIEW[]>()

  const onOpenPinStation = () => {
    if (!controller) return
    const data = getValues()
    setOpenPinStationOnMap(true)
    setPinStationOnMapData({
      siteId: data.siteId ? data.siteId : Number(dataSite?.list[0]?.id),
      siteName: data.siteName ? data.siteName : dataSite?.list[0]?.name || null,
      locationId: data.locationId
        ? data.locationId
        : (dataLocation && dataLocation[0]?.id) || null,
      locationName:
        data.locationName || (dataLocation && dataLocation[0]?.name) || null,
      floorId: data.floorId || null,
      grsCoordinatedLatitude: data.grsCoordinatedLatitude || null,
      grsCoordinatedLongitude: data.grsCoordinatedLongitude || null,
      areaId: data.areaParent?.id || null,
      areaName: data.areaParent?.name || null,
      subAreaId: data.subAreaParent?.id || null,
      subAreaName: data.subAreaParent?.name || null,
    })
  }

  const getOptions =
    (key: keyof IFormControls) =>
    async (PageIndex?: number, PageSize?: number, Keyword?: string) => {
      let data: any | undefined
      switch (key) {
        case 'controller':
          data = await controllerDataConfigApi
            .getAll({
              interfaceId: Number(interfaceId),
              PageIndex,
              PageSize: 0,
              Keyword,
              isRequiredStation: true,
              type: 1,
              stationId: id ? Number(id) : undefined,
              Purpose: await fetchStationDecoderPurposeIds(Number(interfaceId)),
            })
            .then((res) => res.data)
            .catch((e) => {
              NotifyService.error(e)
              return undefined
            })
          break
        case 'plant':
          data = await plantDataConfigApi
            .getAll({
              interfaceId: Number(interfaceId),
              PageIndex,
              PageSize: PageSize || 20,
              Keyword,
            })
            .then((res) => res.data)
            .catch((e) => {
              NotifyService.error(e)
              return undefined
            })
          break
        case 'soil':
          data = await soilDataConfigApi
            .getAll({
              interfaceId: Number(interfaceId),
              PageIndex,
              PageSize: PageSize || 20,
              Keyword,
            })
            .then((res) => res.data)
            .catch((e) => {
              NotifyService.error(e)
              return undefined
            })

          break
        case 'pipe':
          data = await pipeDataConfigApi
            .getPipeDropDown(Number(interfaceId))
            .then((res) => res)
            .catch((e) => {
              NotifyService.error(e)
              return undefined
            })

          break
        case 'emitter':
          data = await emitterDataConfigApi
            .getAll({
              interfaceId: Number(interfaceId),
              PageIndex,
              PageSize: PageSize || 20,
              Keyword,
            })
            .then((res) => res.data)
            .catch((e) => {
              NotifyService.error(e)
              return undefined
            })
          break
        case 'etZone':
          data = await stationDataConfigApi
            .getEtzone()
            .then((res) => {
              const raws = (res.data as EtZone[]).filter(
                (it) => it.interfaceId === interfaceId,
              )
              return {
                data: raws,
                page: 1,
                size: raws.length,
                totalCount: raws.length,
                totalPage: raws.length / raws.length, //totalCount/size
              }
            })
            .catch((e) => {
              NotifyService.error(e)
              return undefined
            })
          break
        case 'areaParent':
          data = await areaApi
            .getAreaParent(Number(interfaceId))
            .then((res) => {
              const raws = res.data as AreaParentType[]
              return {
                data: raws,
                page: 1,
                size: raws.length,
                totalCount: raws.length,
                totalPage: raws.length,
              }
            })
            .catch((e) => {
              NotifyService.error(e)
              return undefined
            })
          break
        case 'subAreaParent':
          data = await areaApi
            .getSubAreaParent(Number(interfaceId), watch('areaParent.id'))
            .then((res) => {
              const raws = res.data as SubAreaParentType[]
              return {
                data: raws,
                page: 1,
                size: raws.length,
                totalCount: raws.length,
                totalPage: raws.length / raws.length, //totalCount/size
              }
            })
            .catch((e) => {
              NotifyService.error(e)
              return undefined
            })
          break
      }
      return data
    }

  const onChangeControl = (_: any) => {
    setValue('floorId', undefined)
    setValue('siteId', undefined)
    setValue('siteName', '')
    setValue('locationId', undefined)
    setValue('locationName', '')
    setValue('grsCoordinatedLatitude', null)
    setValue('grsCoordinatedLongitude', null)
  }

  const onChangeArea = (_: any) => {
    setValue('subAreaParent', {
      id: 0,
      name: '',
    })
    setLoadOptionInitKey(watch('areaParent.id').toString())
  }

  const onError = (err: any) => {
    // eslint-disable-next-line
    console.log(err, 'error')
  }

  useEffect(() => {
    document.querySelectorAll('.spinner-button').forEach((item) => {
      item.setAttribute('type', 'button')
    })
  }, [])

  useEffect(() => {
    if (editItem) {
      reset(
        mapStationDetailToFormValues(editItem, convertFromMetricRawV2),
      )
      setImage(editItem.pictureUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editItem, reset])

  useEffect(() => {
    ;(async () => {
      if (!!editItem && editItem.subArea) {
        getOptions('subAreaParent')
        setValue('subAreaParent', {
          id: editItem.subArea.id,
          name: editItem.subArea.name,
        })
      }
    })()
  }, [editItem])

  useEffect(() => {
    ;(async () => {
      if (dataSite && dataSite.list.length > 0) {
        const res = await locationApi.getBySiteId(dataSite.list[0].id, {
          PageSize: 0,
        })
        if (res) {
          setDataLocation(res.data.data)
        }
      }
    })()
  }, [dataSite])

  return (
    <FormProvider {...form}>
      {isLoadingStation && (
        <Stack
          width="869px"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Stack>
      )}
      {!isLoadingStation && (
        <form
          onSubmit={form.handleSubmit(handleSave, onError)}
          className={clsx(classes.root, classes.form)}
        >
          <Stack maxWidth={'870px'} width={'100%'} sx={{ padding: '37px' }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={'12px'}
            >
              <Typography color="text.main">
                {id ? t('editStation') : t('stationsAddStation')}
              </Typography>
              <Stack direction="row">
                <Button
                  variant="secondary"
                  size="small"
                  sx={{ marginRight: '7px', width: '85px' }}
                  onClick={() => closeSidebar()}
                >
                  {t('commonActionsCancel')}
                </Button>
                <LoadingButton
                  loading={isLoading}
                  size="small"
                  variant="primary"
                  sx={{ minWidth: '85px' }}
                  type="submit"
                >
                  {t('commonActionsSave')}
                </LoadingButton>
              </Stack>
            </Stack>
            <Grid container rowSpacing={1} columnSpacing={2}>
              <Grid item xs={4}>
                <InputLabelField
                  name="description"
                  form={form}
                  label={t('adminStationNameLabel')}
                  placeholder={t('adminStationNameLabel')}
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    marginTop: '8px',
                  }}
                  padding="8px 16px"
                />
              </Grid>
              <Grid item xs={8}></Grid>
              <Grid item xs={4}>
                {' '}
                <Select
                  required
                  minWidth={'100%'}
                  isHasMore
                  filterFunc={false}
                  label={t('formsLabelsDecoder')}
                  name="controller"
                  formatOptionLabel={(item: any) => (
                    <Stack spacing={0.3}>
                      <Stack fontWeight={600}>{item.name}</Stack>
                      {item.address && (
                        <Stack>
                          {item.address}-{item.index}
                        </Stack>
                      )}
                      {item.twiCustomName && (
                        <Stack>{item.twiCustomName}</Stack>
                      )}
                    </Stack>
                  )}
                  placeholder={t('formsPlaceholdersChooseController')}
                  form={form}
                  height={'80px'}
                  // onChange={onChangeControl}
                  getOptions={getOptions('controller')}
                />
              </Grid>
              <Grid item xs={4}>
                <ButtonFieldError
                  name="image"
                  form={form}
                  minWidth={'100%'}
                  marginTop={'8px'}
                  width={'100%'}
                >
                  <LoadingButton
                    loading={isLoadingIMG}
                    loadingPosition="end"
                    variant="contained"
                    component="label"
                    sx={{
                      width: '100%',
                      borderRadius: '4px',
                      backgroundColor: 'background.overrideDefault',
                      color: '#181818',
                      fontSize: '12px',
                      padding: '8px 12px',
                      justifyContent: 'space-between',
                      height: '80px',
                      '&:hover': {
                        backgroundColor: 'background.overrideDefault',
                      },
                    }}
                    endIcon={
                      !image ? (
                        <UploadImage
                          sx={{
                            position: 'relative',
                            top: '4px',
                            right: '3px',
                            color: 'text.main',
                          }}
                        />
                      ) : (
                        <CloseIcon
                          sx={{
                            position: 'relative',
                            top: '4px',
                            right: '2px',
                            left: '2px',
                            color: 'text.main',
                          }}
                          onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            setValue('pictureFileName', '')
                            setValue('image', '')
                            setImage('')
                          }}
                        />
                      )
                    }
                  >
                    <Stack
                      width={'90%'}
                      flexDirection={'row'}
                      justifyContent={'space-between'}
                      alignItems={'center'}
                    >
                      <Stack className={classes.images} width={'75%'}>
                        <Typography>{t('stationImage')}</Typography>
                        {!image && (
                          <input
                            type="file"
                            hidden
                            onChange={handleImgUpload}
                          />
                        )}
                        {editItem || !!image ? (
                          <Tooltip title={form.getValues('pictureFileName')}>
                            <Typography
                              sx={{ mt: '3px' }}
                              className={classes.nameImage}
                            >
                              {form.getValues('pictureFileName')}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <p
                            style={{ margin: 0 }}
                            className={classes.nameImage}
                          >
                            {t('stationsImageUpload')}
                          </p>
                        )}
                      </Stack>
                      {!!image && (
                        <img
                          style={{
                            height: '38px',
                            width: 'fit-content',
                            maxWidth: '56px',
                          }}
                          src={form.getValues('image')}
                          alt=""
                        />
                      )}
                    </Stack>
                  </LoadingButton>
                </ButtonFieldError>
              </Grid>
              <Grid item xs={4}>
                {' '}
                <Select
                  required
                  minWidth={'100%'}
                  isHasMore
                  filterFunc={false}
                  label={t('Station Type')}
                  name="Type"
                  placeholder={t('formsPlaceholdersChooseStationType')}
                  form={form}
                  height={'80px'}
                  bindKey="value"
                  options={STATION_TYPE_OPTIONS}
                />
              </Grid>

              {/* <Grid item xs={4}>
                <ButtonFieldError
                  name="grsCoordinatedLatitude"
                  form={form}
                  minWidth={'100%'}
                  marginTop={'8px'}
                >
                  <Button
                    variant="contained"
                    component="label"
                    disabled={!controller}
                    onClick={onOpenPinStation}
                    sx={{
                      borderRadius: '4px',
                      backgroundColor: 'background.overrideDefault',
                      color: '#181818',
                      fontSize: '12px',
                      width: '100%',
                      padding: '8px 12px',
                      justifyContent: 'space-between',
                      height: '52px',
                      '&:hover': {
                        backgroundColor: 'background.overrideDefault',
                      },
                    }}
                    endIcon={
                      <LocalPinIcon
                        sx={{
                          position: 'relative',
                          top: '4px',
                          right: '3px',
                          color: 'text.main',
                        }}
                      />
                    }
                  >
                    <Stack>
                      <Typography> {t('gpsCoordinates')}</Typography>
                      <Typography mt="3px">
                        {grsCoordinatedLatitude && grsCoordinatedLongitude
                          ? formatPosition(
                              new google.maps.LatLng(
                                grsCoordinatedLatitude,
                                grsCoordinatedLongitude,
                              ),
                            )
                          : t('addCoordinates')}
                      </Typography>
                    </Stack>
                  </Button>
                </ButtonFieldError>
              </Grid> */}
              <Grid item xs={12}>
                <Divider sx={{ margin: '8px 0px' }} />
              </Grid>
            </Grid>
            <Grid container rowSpacing={1} columnSpacing={4}>
              <Grid item xs={4}>
                {' '}
                <Select
                  required
                  minWidth={'100%'}
                  filterFunc={false}
                  label={t('AreasTableHeaderAreas')}
                  name="areaParent"
                  bindLabel="name"
                  placeholder="Select one"
                  form={form}
                  onChange={onChangeArea}
                  getOptions={getOptions('areaParent')}
                />
              </Grid>
              <Grid item xs={4}>
                {' '}
                <Select
                  isDisabled={watch('subAreaParent') === undefined}
                  required
                  minWidth={'100%'}
                  isHasMore
                  filterFunc={false}
                  label={t('formsLabelsSubArea')}
                  name="subAreaParent"
                  bindLabel="name"
                  placeholder="Select one"
                  form={form}
                  getOptions={getOptions('subAreaParent')}
                  key={loadOptionInitKey}
                />
              </Grid>
              <Grid item xs={4}>
                <Select
                  minWidth={'100%'}
                  isHasMore
                  filterFunc={false}
                  label={t('formsLabelsMicroClimate')}
                  name="etZone"
                  bindLabel="name"
                  placeholder="Select one"
                  form={form}
                  // Optional, so a zone picked by mistake has to be removable;
                  // save already sends null for an empty one.
                  isClearable
                  getOptions={getOptions('etZone')}
                  isDisabled={type === 1}
                  sx={{ opacity: type === 1 ? 0.1 : 1 }}
                />
              </Grid>
              <Grid item xs={4}>
                <Select
                  required
                  minWidth={'100%'}
                  isHasMore
                  filterFunc={false}
                  label={t('formsLabelsPlant')}
                  name="plant"
                  bindLabel="description"
                  placeholder="Select one"
                  form={form}
                  getOptions={getOptions('plant')}
                  isDisabled={type === 1}
                  sx={{ opacity: type === 1 ? 0.1 : 1 }}
                />
              </Grid>
              <Grid item xs={4}>
                {' '}
                <Select
                  required
                  minWidth={'100%'}
                  isHasMore
                  filterFunc={false}
                  label={t('formsLabelsSoil')}
                  name="soil"
                  bindLabel="description"
                  placeholder="Select one"
                  form={form}
                  getOptions={getOptions('soil')}
                  isDisabled={type === 1}
                  sx={{ opacity: type === 1 ? 0.1 : 1 }}
                />
              </Grid>
              <Grid item xs={4}>
                <Select
                  required
                  minWidth={'100%'}
                  isHasMore
                  filterFunc={false}
                  label={t('formsLabelsEmitter')}
                  name="emitter"
                  bindLabel="emitter"
                  placeholder={t('formsPlaceholdersChooseEmitter')}
                  form={form}
                  getOptions={getOptions('emitter')}
                  isDisabled={type === 1}
                  sx={{ opacity: type === 1 ? 0.1 : 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ margin: '8px 0px' }} />
              </Grid>
            </Grid>
            <Grid container rowSpacing={1} columnSpacing={4}>
              <Grid item xs={4}>
                {' '}
                <Select
                  required
                  minWidth={'100%'}
                  isHasMore
                  filterFunc={false}
                  label={t('formsLabelsPipe')}
                  name="pipe"
                  bindLabel="description"
                  placeholder="Select one"
                  form={form}
                  getOptions={getOptions('pipe')}
                  isDisabled={type === 1}
                  sx={{ opacity: type === 1 ? 0.1 : 1 }}
                />
              </Grid>
              <Grid item xs={4}>
                <InputLabelField
                  isRequired
                  name="priority"
                  form={form}
                  label={t('formsLabelsPriority')}
                  placeholder="Describe the priority"
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    // width: { xs: '377px' },
                    marginTop: '8px',
                    opacity: type === 1 ? 0.5 : 1,
                  }}
                  padding="8px 16px"
                  disabled={type === 1}
                />
              </Grid>
              <Grid item xs={4}></Grid>
              <Grid item xs={4}>
                {' '}
                <InputLabelField
                  isRequired
                  name="preciptation"
                  form={form}
                  label={`${t(
                    'formsLabelsPreciptationRate',
                  )} (${convertLabelUnit('mm/h')})`}
                  placeholder={`${t(
                    'formsLabelsPreciptationRate',
                  )} (${convertLabelUnit('mm/h')})`}
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    // width: { xs: '377px' },
                    marginTop: '8px',
                    opacity: type === 1 ? 0.5 : 1,
                  }}
                  padding="8px 16px"
                  disabled={type === 1}
                />
              </Grid>
              <Grid item xs={4}>
                {' '}
                <InputLabelField
                  isRequired
                  name="expected"
                  form={form}
                  label={`${t('formsLabelsExpectedFlow')} (${convertLabelUnit(
                    'l/m',
                  )})`}
                  placeholder={`${t(
                    'formsLabelsExpectedFlow',
                  )} (${convertLabelUnit('l/m')})`}
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    // width: { xs: '377px' },
                    marginTop: '8px',
                    opacity: type === 1 ? 0.5 : 1,
                  }}
                  padding="8px 16px"
                  disabled={type === 1}
                />
              </Grid>
              <Grid item xs={4}></Grid>
              <Grid item xs={4}>
                <Box className={classes.default} sx={{ marginTop: '8px', opacity: type === 1 ? 0.5 : 1 }}>
                  <TimeFieldControl
                    form={form}
                    name="defaultRunTime"
                    label={t('formsLabelsDefaultRuntime')}
                    style={{
                      borderRadius: '4px',
                      height: 30,
                    }}
                    showSecond
                    disabled={type === 1}
                  />
                </Box>
                <ErrorMessage
                  sx={{ mt: '36px' }}
                  message={errors.defaultRunTime?.message}
                />
              </Grid>

              <Grid item xs={4}>
                <InputLabelField
                  name="water"
                  form={form}
                  label={`${t('formsLabelsWaterRequired')} (${convertLabelUnit(
                    'mm',
                  )})`}
                  placeholder="Water required"
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    // width: { xs: '377px' },
                    marginTop: '8px',
                    opacity: type === 1 ? 0.5 : 1,
                  }}
                  padding="8px 16px"
                  disabled={type === 1}
                />
              </Grid>
              <Grid item xs={4} style={{ display: 'none' }}>
                <InputLabelField
                  disabled
                  name="emitter.efficiency"
                  value={emitter?.efficiency}
                  label={t('formsLabelsEmitterEfficiency')}
                  placeholder={t('formsLabelsEmitterEfficiency')}
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    // width: { xs: '377px' },
                    marginTop: '8px',
                  }}
                  padding="8px 16px"
                />
              </Grid>

              {/* <Grid item xs={4}>
                <InputLabelField
                  disabled
                  label={t('formsLabelsAddress')}
                  placeholder={t('formsLabelsAddress')}
                  name="controller.address"
                  value={controller?.address}
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    // width: { xs: '377px' },
                    marginTop: '8px',
                  }}
                  padding="8px 16px"
                />
              </Grid> */}

              {/* <Grid item xs={4}>
                {' '}
                <InputLabelField
                  disabled
                  name="controller.index"
                  value={controller?.index}
                  label={t('formsLabelsIndex')}
                  placeholder={t('formsLabelsIndex')}
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    // width: { xs: '377px' },
                    marginTop: '8px',
                  }}
                  padding="8px 16px"
                />
              </Grid> */}

              {/* <Grid item xs={4}>
                <InputLabelField
                  name="plant_water"
                  form={form}
                  label={`Plant Available Water (${convertLabelUnit('mm')})`}
                  placeholder="Plant Available Water"
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    // width: { xs: '377px' },
                    marginTop: '8px',
                  }}
                  padding="8px 16px"
                />
              </Grid> */}
              {/* <Grid item xs={4}>
                {' '}
                <InputLabelField
                  disabled
                  name="controller.decoderType"
                  value={controller?.decoderType}
                  label={t('formsLabelsDecoderType')}
                  placeholder={t('formsLabelsDecoderType')}
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    // width: { xs: '377px' },
                    marginTop: '8px',
                  }}
                  padding="8px 16px"
                />
              </Grid> */}

              {/* <Grid item xs={4}>
                {' '}
                <InputLabelField
                  disabled
                  name="controller.type"
                  value={controller?.type}
                  label={t('formsLabelsType')}
                  placeholder={t('formsLabelsType')}
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    // width: { xs: '377px' },
                    marginTop: '8px',
                  }}
                  padding="8px 16px"
                />
              </Grid> */}

              {/* <Grid item xs={4}>
                <InputLabelField
                  name="landscape"
                  form={form}
                  label="Landscape factor (%)"
                  placeholder="Landscape factor"
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    // width: { xs: '377px' },
                    marginTop: '8px',
                  }}
                  padding="8px 16px"
                />
              </Grid> */}
              {/* <Grid item xs={4}>
                <InputLabelField
                  disabled
                  name="controller.configuration"
                  value={controller?.configuration}
                  label="Decoder Configure"
                  placeholder="Decoder Configure"
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    // width: { xs: '377px' },
                    marginTop: '8px',
                  }}
                  padding="8px 16px"
                />
              </Grid> */}

              {/* <Grid item xs={4}>
                <InputLabelField
                  disabled
                  name="etZone.etNumber"
                  value={etZone?.etNumber}
                  label="ET Number"
                  placeholder="ET Number"
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    // width: { xs: '377px' },
                    marginTop: '8px',
                  }}
                  padding="8px 16px"
                />
              </Grid> */}

              {/* <Grid item xs={4}>
                <InputLabelField
                  name="runtime"
                  form={form}
                  label="Runtime day override (min)"
                  placeholder="Runtime day override"
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    // width: { xs: '377px' },
                    marginTop: '8px',
                  }}
                  padding="8px 16px"
                />
              </Grid> */}
            </Grid>
            {id && editItem ? (
              <StationDeletionAction
                key={`${interfaceId}:${id}`}
                interfaceId={Number(interfaceId)}
                station={editItem}
                disabled={isLoading}
                onDeleted={closeSidebar}
              />
            ) : null}
          </Stack>
        </form>
      )}
      {openPinStationOnMap && interfaceId && (
        <PinStationOnMap
          open={true}
          station={pinStationOnMapData}
          interfaceId={Number(interfaceId)}
          onClose={onClosePinStation}
          onChangePosition={onChangePosition}
        />
      )}
    </FormProvider>
  )
}

export default StationsView
